import { useFormikContext, getIn } from 'formik';

import TextField from '@mui/material/TextField';
import Grid from '@mui/material/Grid';

import { EMPTY_STRING } from '@e-socna/empty';

import { getTouchedError } from '../../../../utils/get-touched-error.util';
import type { AddressForm } from '../../types/address-form.type';

interface Props {
  readonly id: string;
}

// eslint-disable-next-line no-empty-pattern
export const AddressPostalInfoFormStep = ({}: Readonly<Props>) => {
  const { errors, handleBlur, handleChange, touched, values } =
    useFormikContext<AddressForm>();

  return (
    <Grid container spacing={2} sx={{ alignContent: 'flex-start' }}>
      <Grid
        size={{
          sm: 3,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'postalInfo.streetNumber') &&
              getIn(touched, 'postalInfo.streetNumber')
            )
          }
          fullWidth
          helperText={getTouchedError(
            errors,
            touched,
            'postalInfo.streetNumber',
          )}
          id="address-postal-info-street-number"
          label="Numéro de rue"
          name="postalInfo.streetNumber"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          type="text"
          value={values.postalInfo?.streetNumber ?? EMPTY_STRING}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          sm: 9,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'postalInfo.route') &&
              getIn(touched, 'postalInfo.route')
            )
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'postalInfo.route')}
          id="address-postal-info-route"
          label="Nom de la rue"
          name="postalInfo.route"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          type="text"
          value={values.postalInfo?.route ?? EMPTY_STRING}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'postalInfo.building') &&
              getIn(touched, 'postalInfo.building')
            )
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'postalInfo.building')}
          id="address-postal-info-building"
          label="Bâtiment"
          name="postalInfo.building"
          onBlur={handleBlur}
          onChange={handleChange}
          required={false}
          type="text"
          value={values.postalInfo?.building ?? EMPTY_STRING}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'postalInfo.additional') &&
              getIn(touched, 'postalInfo.additional')
            )
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'postalInfo.additional')}
          id="address-postal-info-additional"
          label="Informations Supplémentaires"
          name="postalInfo.additional"
          onBlur={handleBlur}
          onChange={handleChange}
          required={false}
          type="text"
          value={values.postalInfo?.additional ?? EMPTY_STRING}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          sm: 3,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'postalInfo.postalCode') &&
              getIn(touched, 'postalInfo.postalCode')
            )
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'postalInfo.postalCode')}
          id="address-postal-info-postal-code"
          label="Code postal"
          name="postalInfo.postalCode"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          type="text"
          value={values.postalInfo?.postalCode ?? EMPTY_STRING}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          sm: 6,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'postalInfo.locality') &&
              getIn(touched, 'postalInfo.locality')
            )
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'postalInfo.locality')}
          id="address-postal-info-locality"
          label="Localité"
          name="postalInfo.locality"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          type="text"
          value={values.postalInfo?.locality ?? EMPTY_STRING}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          sm: 3,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'postalInfo.inseeCode') &&
              getIn(touched, 'postalInfo.inseeCode')
            )
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'postalInfo.inseeCode')}
          id="address-postal-info-insee-code"
          label="Code INSEE"
          name="postalInfo.inseeCode"
          onBlur={handleBlur}
          onChange={handleChange}
          required={false}
          type="text"
          value={values.postalInfo?.inseeCode ?? EMPTY_STRING}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          sm: 4,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'postalInfo.businessPostCode.postalCode') &&
              getIn(touched, 'postalInfo.businessPostCode.postalCode')
            )
          }
          fullWidth
          helperText={
            getIn(errors, 'postalInfo.businessPostCode.postalCode') as string
          }
          id="address-postal-info-business-post-code-postal-code"
          label="Code postal CEDEX"
          name="postalInfo.businessPostCode.postalCode"
          onBlur={handleBlur}
          onChange={handleChange}
          required={false}
          type="text"
          value={
            values.postalInfo?.businessPostCode?.postalCode ?? EMPTY_STRING
          }
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          sm: 4,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(
                errors,
                'postalInfo.businessPostCode.distributorOfficeNumber',
              ) &&
              getIn(
                touched,
                'postalInfo.businessPostCode.distributorOfficeNumber',
              )
            )
          }
          fullWidth
          helperText={
            getIn(
              errors,
              'postalInfo.businessPostCode.distributorOfficeNumber',
            ) as string
          }
          id="address-postal-info-business-post-code-distributor-office-number"
          label="Bureau distributeur CEDEX"
          name="postalInfo.businessPostCode.distributorOfficeNumber"
          onBlur={handleBlur}
          onChange={handleChange}
          required={false}
          type="text"
          value={
            values.postalInfo?.businessPostCode?.distributorOfficeNumber ??
            EMPTY_STRING
          }
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          sm: 4,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'postalInfo.postOfficeBox') &&
              getIn(touched, 'postalInfo.postOfficeBox')
            )
          }
          fullWidth
          helperText={getTouchedError(
            errors,
            touched,
            'postalInfo.postOfficeBox',
          )}
          id="address-postal-info-post-office-box"
          label="Boîte Postale"
          name="postalInfo.postOfficeBox"
          onBlur={handleBlur}
          onChange={handleChange}
          required={false}
          type="text"
          value={values.postalInfo?.postOfficeBox ?? EMPTY_STRING}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          sm: 4,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'postalInfo.department') &&
              getIn(touched, 'postalInfo.department')
            )
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'postalInfo.department')}
          id="address-postal-info-department"
          label="Département"
          name="postalInfo.department"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          type="text"
          value={values.postalInfo?.department ?? EMPTY_STRING}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          sm: 4,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'postalInfo.region') &&
              getIn(touched, 'postalInfo.region')
            )
          }
          fullWidth
          helperText={getTouchedError(errors, touched, 'postalInfo.region')}
          id="address-postal-info-region"
          label="Région"
          name="postalInfo.region"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          type="text"
          value={values.postalInfo?.region ?? EMPTY_STRING}
          variant="outlined"
        />
      </Grid>
      <Grid
        size={{
          sm: 4,
          xs: 12,
        }}
        sx={{ pt: 1, pb: 1 }}
      >
        <TextField
          error={
            !!(
              getIn(errors, 'postalInfo.countryAlpha2') &&
              getIn(touched, 'postalInfo.countryAlpha2')
            )
          }
          fullWidth
          helperText={getTouchedError(
            errors,
            touched,
            'postalInfo.countryAlpha2',
          )}
          id="address-postal-info-country-alpha-2"
          label="Code Pays"
          name="postalInfo.countryAlpha2"
          onBlur={handleBlur}
          onChange={handleChange}
          required
          type="text"
          value={values.postalInfo?.countryAlpha2 ?? EMPTY_STRING}
          variant="outlined"
        />
      </Grid>
    </Grid>
  );
};
